import { memo } from 'react';
import styled, { css } from 'styled-components';
import { color } from 'styled-system';
import theme from 'styled-theming';
import Link from '../router/query-encoded-link.js';
import breakpoint, { getZindexByType } from '../../utils/style-utils';
import { motion } from 'framer-motion';

const LinkWrapper = styled(motion.span)`
	width: ${({ maxWidth }) => maxWidth && `100%`};
	a {
		display: block;
	}
`;

const AnimatedLink = props => {
	return (
		<LinkWrapper
			maxWidth={props.maxWidth}
			whileTap={{ y: -5, transition: { type: 'spring', stiffness: 200 } }}
			tabIndex={props.tabIndex || -1} // Prevent focus on LinkWrapper and as a result a double focus because Link is also focusable.
		>
			<Link {...props}>{props.children}</Link>
		</LinkWrapper>
	);
};

const ExternalLink = ({ to, ...rest }) => (
	<motion.a whileTap={{ y: -5, transition: { type: 'spring', stiffness: 200 } }} href={to} {...rest} />
);

const dataAt = props => ({
	'data-at': props.dataAt
});

const secondaryFiltersBrandException = theme('brand', {
	mobilebet: css`
		background-color: transparent;
		${breakpoint.lg`
			background-color: var(--filter-bg-secondary);
		`}
		background-color: transparent;
	`
});
const Filters = memo(styled.div`
	background-color: var(--filter-bg);
	padding: 0 var(--smallerFrame);
	display: grid;
	${props =>
		props.secondary &&
		css`
			background-color: var(--filter-bg-secondary);
			--filter-active: var(--filter-active-secondary);
			--filter-idle: var(--filter-idle-secondary);
			--filter-hover: var(--filter-hover-secondary);
			--filter-bar-active: var(--filter-bar-active-secondary);
			${secondaryFiltersBrandException}
		`};
	${breakpoint.lg`
			padding: 0 var(--biggerFrame);
		`};
`);

const FiltersInnerNoNavRight = memo(styled.nav`
	display: flex;
	justify-content: space-evenly;
	max-width: 100%;
	${breakpoint.lg`
		justify-content: left;
    	max-width: 375px;
    	min-height: 48px;
  	`};
`);

const FiltersInner = memo(styled.nav.attrs(dataAt)`
	display: flex;
	justify-content: space-evenly;
	max-width: 100%;
	${props => (props.alignnavright ? 'justify-self: end;' : '')};
	${breakpoint.lg`
		justify-content: left;
    	min-height: var(--subHeaderHeight);
  	`};
	${color}
	${props =>
		props.borderAfter &&
		css`
			&::after {
				content: '';
				height: 1px;
				width: 100%;
				background-color: var(--filter-bar-bottom-border);
				position: absolute;
				bottom: 0;
				z-index: 0;
			}
		`};
`);

const LinkStyled = link => styled(link)`
	${props => (props.disabled ? 'color: var(--filter-disabled);' : 'color: var(--filter-idle);')}
	font: var(--label-bold);
	margin-right: 24px;
	padding: 14px 0;
	${props =>
		props.retainCase
			? css`
					text-transform: none;
				`
			: css`
					text-transform: lowercase;
					:first-letter {
						text-transform: uppercase;
					}
				`}
	position: relative;
	white-space: nowrap;
	width: 100%;
	text-align: center;
	&:hover {
		@media (hover) {
			${props => (props.disabled ? 'color: var(--filter-disabled);' : 'color: var(--filter-hover);')};
		}
	}
	&.active {
		color: var(--filter-active) !important;
		background-color: var(--filter-active-bg);
		${breakpoint.lg`
			background-color: transparent;
		`};
		&:after {
			background-color: var(--filter-bar-active);
			bottom: 0;
			content: '';
			height: 3px;
			left: 0;
			position: absolute;
			width: 100%;
			z-index: 1;
		}
	}
	${props => (props.alignitemright ? 'margin-left: 24px;' : 'margin-right: 24px;')} &:hover {
		@media (hover) {
			${props =>
				props.disabled
					? 'color: var(--filter-disabled);'
					: 'color: var(--filter-hover, rgba(var(--base-02), 1));'};
		}
	}
	${breakpoint.lg`
	text-align: left;
	width: auto;
	text-transform: var(--brand-text-transform, none);
`};
`;
const StyledExternalLink = memo(LinkStyled(ExternalLink));

const StyledAnimatedLink = ({ retainCase, ...props }) => <AnimatedLink {...props} />;

const FilterLink = memo(LinkStyled(StyledAnimatedLink));

const ProductFilters = styled(Filters)`
	padding: 0;
	width: 100%;
	height: var(--subHeaderHeight);
	position: relative;

	span {
		width: 50%;
	}
`;

const iosProductFilterCss = css`
	height: calc(var(--subHeaderHeight) + 40px);
	padding-bottom: 40px;

	.unauthenticated-header {
		bottom: calc(var(--unauth-header-position, 0) + 34px);
	}
`;

const StyledProductNavbarFilters = styled(Filters).attrs(dataAt)`
	bottom: 0;
	grid-area: subheader;
	width: 100%;
	z-index: ${getZindexByType('product-filters')};
	transition:
		transform 300ms ease-out,
		height 300ms ease-out;
	height: var(--smallerSubHeaderHeight);
	.navbar-right {
		a {
			color: var(--filter-support-links, rgba(var(--base-01), 1));
		}
	}
	.unauthenticated-header {
		transition: bottom 300ms ease-out;
	}

	${breakpoint.lg`
		border-bottom: var(--filter-border-primary, rgba(var(--base-09), 1));
		background-color: var(--filter-bg);
		height: var(--subHeaderHeight);
		bottom: auto;
		grid-area: subheader;
		grid-template-columns: 2fr 1fr;
		top: ${props =>
			props.hasRegulationsHeader ? `calc(var(--headerHeight) + var(--regulationHeight))` : `var(--headerHeight)`};

		.unauthenticated-header {
			display: none;
		}

		${props =>
			props.sportsbook &&
			css`
				border-bottom: none;
				top: auto;
				bottom: 0;
				grid-template-columns: 2fr;
			`};
	`};

	${props => props.isIOS && props.safariToolbarIsHidden && iosProductFilterCss}
`;

const ProductNavbarFilters = memo(StyledProductNavbarFilters);

export {
	Filters,
	FiltersInner,
	FiltersInnerNoNavRight,
	FilterLink,
	StyledExternalLink,
	ProductFilters,
	ProductNavbarFilters
};
